import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, X, ChevronLeft, ChevronRight, ArrowRight, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchNews, resolveImg } from '../api';
import { useSiteImages } from '../context/SiteImagesContext';
import { usePageMeta } from '../hooks/usePageMeta';

const Actualites = () => {
  usePageMeta({ title: 'Actualités - GR La Saline', description: 'Retrouvez toutes les actualités du club GR La Saline : événements, compétitions, stages et vie du club.' });
  const images = useSiteImages();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null); // { item, photoIndex } for lightbox
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  useEffect(() => {
    fetchNews().then((d) => setItems(d || [])).catch(() => {});
  }, []);

  // Scroll to hash target once items are rendered
  useEffect(() => {
    if (!items.length || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.2 });
      else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }, [items]);

  const openLightbox = (item, idx) => setSelected({ item, photoIndex: idx });
  const closeLightbox = () => setSelected(null);
  const nextPhoto = () => setSelected((s) => ({ ...s, photoIndex: (s.photoIndex + 1) % getPhotos(s.item).length }));
  const prevPhoto = () => setSelected((s) => ({ ...s, photoIndex: (s.photoIndex - 1 + getPhotos(s.item).length) % getPhotos(s.item).length }));

  const getPhotos = (item) => {
    const all = [];
    if (item.image) all.push(item.image);
    if (item.images && item.images.length) all.push(...item.images);
    return all.filter(Boolean).slice(0, 5);
  };

  return (
    <div className="bg-black min-h-screen" data-testid="actualites-page">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={images.actualites_hero || images.whatisgr_hero} alt="Actualités" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
        </div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="inline-block px-4 py-2 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-full text-fuchsia-300 text-sm font-medium tracking-wider mb-6">
            Toutes les nouvelles
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="text-5xl md:text-7xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">Actualités</span>
          </motion.h1>
        </div>
      </section>

      {/* News list */}
      <section className="relative py-24" ref={ref}>
        <div className="container mx-auto px-6 max-w-5xl">
          {items.length === 0 && <p className="text-center text-white/50">Aucune actualité pour le moment.</p>}

          <div className="space-y-16">
            {items.map((item, idx) => {
              const photos = getPhotos(item);
              return (
                <motion.article
                  key={item.id}
                  id={`news-${item.id}`}
                  initial={{ opacity: 0, y: 40 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: Math.min(idx, 5) * 0.05 }}
                  className="scroll-mt-24 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-fuchsia-500/30 transition-all duration-500"
                  data-testid={`news-article-${item.id}`}
                >
                  <div className="p-8">
                    <div className="flex flex-wrap items-center gap-3 text-sm text-white/50 mb-3">
                      <span className="px-3 py-1 bg-fuchsia-500/90 rounded-full text-white text-xs font-medium">{item.category}</span>
                      <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{item.title}</h2>
                    {item.excerpt && <p className="text-white/70 text-lg mb-4">{item.excerpt}</p>}
                    {item.content && <p className="text-white/60 leading-relaxed whitespace-pre-line">{item.content}</p>}

                    {item.link && (
                      <div className="mt-6">
                        {/^https?:\/\//i.test(item.link) ? (
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 bg-fuchsia-500/20 border border-fuchsia-500/40 rounded-lg text-fuchsia-300 font-medium hover:bg-fuchsia-500/30 transition-colors" data-testid={`news-link-${item.id}`}>
                            {item.linkLabel || 'En savoir plus'} <ArrowUpRight size={16} />
                          </a>
                        ) : (
                          <Link to={item.link} className="inline-flex items-center gap-2 px-5 py-2.5 bg-fuchsia-500/20 border border-fuchsia-500/40 rounded-lg text-fuchsia-300 font-medium hover:bg-fuchsia-500/30 transition-colors" data-testid={`news-link-${item.id}`}>
                            {item.linkLabel || 'En savoir plus'} <ArrowRight size={16} />
                          </Link>
                        )}
                      </div>
                    )}

                    {photos.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-3 mt-6 max-w-3xl mx-auto">
                        {photos.map((p, pi) => (
                          <button
                            key={pi}
                            onClick={() => openLightbox(item, pi)}
                            className="group relative h-40 md:h-48 rounded-xl overflow-hidden bg-white/5 border border-white/10 hover:border-fuchsia-500/50 transition-all"
                            data-testid={`news-photo-${item.id}-${pi}`}
                          >
                            <img src={resolveImg(p)} alt={`${item.title} - photo ${pi + 1}`} className="h-full w-auto object-contain group-hover:scale-105 transition-transform duration-500" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selected && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" onClick={closeLightbox} data-testid="news-lightbox">
          <button className="absolute top-6 right-6 text-white/70 hover:text-white z-10" onClick={closeLightbox}><X size={28} /></button>
          {getPhotos(selected.item).length > 1 && (
            <>
              <button className="absolute left-6 text-white/70 hover:text-white z-10" onClick={(e) => { e.stopPropagation(); prevPhoto(); }}><ChevronLeft size={40} /></button>
              <button className="absolute right-6 text-white/70 hover:text-white z-10" onClick={(e) => { e.stopPropagation(); nextPhoto(); }}><ChevronRight size={40} /></button>
            </>
          )}
          <img
            src={resolveImg(getPhotos(selected.item)[selected.photoIndex])}
            alt="Actualité"
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Actualites;
